import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Article, Category, StrapiData } from "../types";
import { useRouter } from "../context/RouterContext";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Layers,
} from "lucide-react";
import { api } from "../services/api";
import { useLocale } from "../context/LocaleContext";
import { ArticleCard } from "../components/ArticleCard";
import { NewsletterSubscribe } from "../components/NewsletterSubscribe";

export function ArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [pageData, setPageData] = useState<{
    title: string;
    description: string;
    searchPlaceholder: string;
    filterByCategory: string;
    allCategories: string;
    showingArticles: string;
    article: string;
    articles: string;
    withFilters: string;
    noArticlesFound: string;
    clearAllFilters: string;
    noArticlesAvailable: string;
    readArticleText: string;
    minReadText: string;
  } | null>(null);
  const { navigate } = useRouter();
  const { locale } = useLocale();
  const pageSize = 9;

  // Fetch articles page data
  useEffect(() => {
    async function fetchPageData() {
      try {
        const response = await api.getArticlesPage(locale);
        //console.log("Articles page API response:", response);
        // Strapi single types return: { data: { id, attributes: {...} } } or { data: {...} }
        if (response?.data) {
          // Check if data has attributes property (Strapi v4 format)
          const pageContent = response.data.attributes || response.data;
          //console.log("Articles page content:", pageContent);
          setPageData(pageContent);
        }
      } catch (error) {
        console.error("Failed to fetch articles page data:", error);
      }
    }
    fetchPageData();
  }, [locale]);

  // Fetch categories
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response: StrapiData<Category[]> = await api.getCategories(
          locale,
          {
            pagination: { pageSize: 100 },
          }
        );
        //console.log("Categories API response:", response);

        // Deduplicate by slug since we might have duplicate entries
        const seen = new Set<string>();
        const uniqueCategories = (response.data || []).filter((cat) => {
          if (seen.has(cat.slug)) {
            return false;
          }
          seen.add(cat.slug);
          return true;
        });

        //console.log("Unique categories:", uniqueCategories);
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    }
    fetchCategories();
  }, [locale]);

  // Fetch articles
  useEffect(() => {
    async function fetchArticles() {
      setLoading(true);
      try {
        const filters: any = {};

        // Add search filter
        if (searchQuery) {
          filters.title = { $containsi: searchQuery };
        }

        // Add category filter
        if (selectedCategory !== null) {
          filters.category = { id: { $eq: selectedCategory } };
        }

        //console.log("Fetching articles with filters:", filters);

        const response: StrapiData<Article[]> = await api.getArticles(locale, {
          pagination: {
            page: currentPage,
            pageSize: pageSize,
          },
          filters: Object.keys(filters).length > 0 ? filters : undefined,
        });

        setArticles(response.data || []);
        if (response.meta?.pagination) {
          setTotalPages(response.meta.pagination.pageCount);
          setTotalArticles(response.meta.pagination.total);
        }
      } catch (error) {
        console.error("Failed to fetch articles:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchArticles();
  }, [locale, currentPage, searchQuery, selectedCategory]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCategorySelect = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  const activeFiltersCount = (searchQuery ? 1 : 0) + (selectedCategory ? 1 : 0);

  return (
    <div className="min-h-screen bg-black py-24 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <BookOpen className="w-16 h-16 text-red-600 mx-auto mb-2" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {pageData?.title ? (
              // Split the title and make the second word red
              (() => {
                const words = pageData.title.split(" ");
                if (words.length > 1) {
                  return (
                    <>
                      {words[0]}{" "}
                      <span className="text-red-600">
                        {words.slice(1).join(" ")}
                      </span>
                    </>
                  );
                }
                return pageData.title;
              })()
            ) : (
              <>
                DevOps <span className="text-red-600">Articles</span>
              </>
            )}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-4">
            {pageData?.description ||
              "In-depth guides, tutorials, and best practices for modern DevOps workflows. Stay updated with the latest trends and techniques."}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={
                pageData?.searchPlaceholder || "Search articles by title..."
              }
              className="w-full pl-12 pr-12 py-4 bg-gray-900/50 border border-red-900/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-600/50 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Layers className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold text-white">
              {pageData?.filterByCategory || "Filter by Category"}
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleCategorySelect(null)}
              className={`px-4 py-2 rounded-lg border transition-all ${
                selectedCategory === null
                  ? "bg-red-600 border-red-600 text-white"
                  : "bg-gray-900/30 border-red-900/20 text-gray-400 hover:border-red-600/50 hover:text-red-600"
              }`}
            >
              {pageData?.allCategories || "All Categories"}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`px-4 py-2 rounded-lg border transition-all flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? "bg-red-600 border-red-600 text-white"
                    : "bg-gray-900/30 border-red-900/20 text-gray-400 hover:border-red-600/50 hover:text-red-600"
                }`}
              >
                {category.icon && <span>{category.icon}</span>}
                {category.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Active Filters & Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8 flex items-center justify-between p-4 bg-gray-900/30 border border-red-900/20 rounded-xl"
        >
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">
              {totalArticles > 0 ? (
                <>
                  {pageData?.showingArticles || "Showing"} {totalArticles}{" "}
                  {totalArticles === 1
                    ? pageData?.article || "article"
                    : pageData?.articles || "articles"}
                  {activeFiltersCount > 0 &&
                    ` ${pageData?.withFilters || "with filters"}`}
                </>
              ) : (
                pageData?.noArticlesFound || "No articles found"
              )}
            </span>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-500 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                {pageData?.clearAllFilters || "Clear all filters"}
              </button>
            )}
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {pageData?.noArticlesAvailable || "No articles available yet."}
            </p>
          </motion.div>
        ) : (
          <>
            {/* Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {articles.map((article, idx) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  index={idx}
                  onClick={() => {
                    //console.log("Article clicked:", article.slug);
                    navigate("article", { slug: article.slug });
                  }}
                  readArticleText={pageData?.readArticleText}
                  minReadText={pageData?.minReadText}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-center gap-4"
              >
                <div className="flex justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-red-900/20 bg-gray-900/50 text-gray-400 hover:text-red-600 hover:border-red-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <div className="flex gap-2">
                    {Array.from(
                      { length: Math.min(totalPages, 10) },
                      (_, i) => {
                        // Show first page, last page, current page and surrounding pages
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`w-10 h-10 rounded-lg border transition-all ${
                                currentPage === page
                                  ? "bg-red-600 border-red-600 text-white"
                                  : "border-red-900/20 bg-gray-900/50 text-gray-400 hover:text-red-600 hover:border-red-600/50"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="w-10 h-10 flex items-center justify-center text-gray-600"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      }
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-red-900/20 bg-gray-900/50 text-gray-400 hover:text-red-600 hover:border-red-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-gray-500 text-sm">
                  Showing {(currentPage - 1) * pageSize + 1}-
                  {Math.min(currentPage * pageSize, totalArticles)} of{" "}
                  {totalArticles} articles
                </p>
              </motion.div>
            )}
          </>
        )}

        {/* Newsletter Subscribe Section */}
        {articles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 max-w-2xl mx-auto"
          >
            <NewsletterSubscribe />
          </motion.div>
        )}
      </div>
    </div>
  );
}
